/**
 * AttachmentPicker — reusable file picker / drag-and-drop component.
 *
 * Two variants:
 *
 *   "dialog"   — click-to-pick button + 88px image preview tiles
 *                (used by SupportRequestDialog for screenshot uploads)
 *
 *   "composer" — paperclip icon button + tiny chip row above the input
 *                (used by ChatPanel for CoPilot file uploads)
 *
 * The component is uncontrolled wrt server state — the parent owns the
 * `draft` list (a `DraftAttachment[]`) and pulls in the `onAdd` / `onRemove`
 * callbacks.  This keeps the picker dumb enough to be reused, and lets the
 * parent thread upload progress through the `uploadState` field on each
 * draft so we render a single source of truth.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';

import type { DraftAttachment } from './types';

const MIB = 1024 * 1024;

export type AttachmentPickerVariant = 'dialog' | 'composer' | 'inline';

export interface AttachmentPickerProps {
  variant?: AttachmentPickerVariant;
  /** Files already chosen.  Owned by the parent. */
  drafts: DraftAttachment[];
  /** Append-to-drafts callback after validation passes. */
  onAdd: (files: File[]) => void;
  /** Remove a single draft by id. */
  onRemove: (id: string) => void;
  /** When true, refuses new picks and dims controls. */
  disabled?: boolean;

  /** Max files (after this is reached, the button disables).  Defaults to 10. */
  maxFiles?: number;
  /** Per-file size cap in bytes.  Defaults to 25 MB. */
  maxBytesPerFile?: number;
  /** Cumulative size cap in bytes.  Optional. */
  maxTotalBytes?: number;

  /** Accept attribute (e.g. "image/*" or our CSV/PDF list). */
  accept?: string;
  /** Friendly hint text shown below the button. */
  hint?: string;
  /** Validate every file beyond the type check (returns error message). */
  validate?: (file: File) => string | null;

  /** Show 88px image preview tiles instead of compact chips. */
  imagePreview?: boolean;

  /** Called when validation fails. */
  onValidationError?: (msg: string) => void;
}

/**
 * Stable client-side id for a draft.  Includes filename for debuggability.
 */
function draftId(file: File, index: number): string {
  return `${Date.now()}-${index}-${file.name}`;
}

export default function AttachmentPicker(props: AttachmentPickerProps) {
  const {
    variant = 'composer',
    drafts,
    onAdd,
    onRemove,
    disabled = false,
    maxFiles = 10,
    maxBytesPerFile = 25 * MIB,
    maxTotalBytes,
    accept,
    hint,
    validate,
    imagePreview = false,
    onValidationError,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Run validation against a FileList; return only the files that pass.
   * The first validation failure is reported via `onValidationError` so
   * the parent (which owns the surrounding form) can route the message
   * into its preferred surface (Alert / Snackbar / toast).
   */
  const validateAndForward = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const accepted: File[] = [];
      let total = drafts.reduce((n, d) => n + d.file.size, 0);
      const slotsRemaining = Math.max(0, maxFiles - drafts.length);
      let firstError: string | null = null;

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];

        if (accepted.length >= slotsRemaining) {
          firstError ??= `You can attach at most ${maxFiles} files.`;
          break;
        }
        if (file.size > maxBytesPerFile) {
          firstError ??= `"${file.name}" is too large (max ${Math.round(maxBytesPerFile / MIB)} MB per file).`;
          continue;
        }
        if (maxTotalBytes != null && total + file.size > maxTotalBytes) {
          firstError ??= `Total size exceeds ${Math.round(maxTotalBytes / MIB)} MB.`;
          break;
        }
        if (validate) {
          const err = validate(file);
          if (err) {
            firstError ??= err;
            continue;
          }
        }
        total += file.size;
        accepted.push(file);
      }

      if (firstError && onValidationError) onValidationError(firstError);
      if (accepted.length > 0) onAdd(accepted);

      // Allow re-picking the same file (input retains its value otherwise).
      if (inputRef.current) inputRef.current.value = '';
    },
    [drafts, maxFiles, maxBytesPerFile, maxTotalBytes, validate, onAdd, onValidationError],
  );

  /** Revoke any object URLs we created when the component unmounts. */
  useEffect(
    () => () => {
      drafts.forEach((d) => {
        if (d.previewUrl) {
          try { URL.revokeObjectURL(d.previewUrl); } catch { /* ignore */ }
        }
      });
    },
    // We only clean up on unmount; the picker doesn't own URL lifecycle
    // (parent does via the hook), but this keeps preview-only dialog use
    // safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // ── Drag-and-drop  ───────────────────────────────────────────────────
  const [dragOver, setDragOver] = React.useState(false);
  const onDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    validateAndForward(e.dataTransfer.files);
  };

  const atCapacity = drafts.length >= maxFiles;
  const pickerLabel = variant === 'dialog'
    ? `Attach files (${drafts.length}/${maxFiles})`
    : '';

  return (
    <Box
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        position: 'relative',
        ...(dragOver && {
          outline: '2px dashed',
          outlineColor: 'primary.main',
          outlineOffset: -2,
          borderRadius: 1,
          bgcolor: 'action.hover',
        }),
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        hidden
        onChange={(e) => validateAndForward(e.target.files)}
      />

      {variant === 'dialog' && (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={imagePreview ? <AddPhotoAlternateOutlined /> : <AttachFileRounded />}
            onClick={() => inputRef.current?.click()}
            disabled={disabled || atCapacity}
            sx={{ textTransform: 'none', mb: drafts.length ? 1.5 : 0 }}
          >
            {pickerLabel}
          </Button>
          {hint && (
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
              {hint}
            </Typography>
          )}
        </>
      )}

      {variant === 'composer' && (
        <Tooltip
          title={atCapacity ? `Max ${maxFiles} files per thread` : 'Attach files'}
          placement="top"
        >
          {/* span wrapper preserves tooltip when button is disabled */}
          <span>
            <IconButton
              size="small"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || atCapacity}
              aria-label="Attach files"
            >
              <AttachFileRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {variant === 'inline' && (
        <Button
          variant="text"
          size="small"
          startIcon={<AttachFileRounded fontSize="small" />}
          onClick={() => inputRef.current?.click()}
          disabled={disabled || atCapacity}
          sx={{ textTransform: 'none' }}
        >
          Attach
        </Button>
      )}

      {drafts.length > 0 && imagePreview && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {drafts.map((d) => (
            <Box
              key={d.id}
              sx={{
                position: 'relative',
                width: 88,
                height: 88,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: d.uploadState === 'error' ? 'error.main' : 'divider',
                flexShrink: 0,
                opacity: d.uploadState === 'uploading' ? 0.6 : 1,
              }}
            >
              {d.previewUrl && (
                <Box
                  component="img"
                  src={d.previewUrl}
                  alt=""
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              )}
              <IconButton
                size="small"
                aria-label="Remove file"
                onClick={() => onRemove(d.id)}
                disabled={disabled}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(0,0,0,0.45)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                  p: 0.35,
                }}
              >
                <DeleteOutlineRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// Helper for the parent to construct a DraftAttachment from a raw File.
export function makeDraftFromFile(file: File, index = 0, withImagePreview = false): DraftAttachment {
  return {
    id: draftId(file, index),
    file,
    previewUrl: withImagePreview ? URL.createObjectURL(file) : undefined,
    uploadState: 'queued',
  };
}
